// One group in Settings: the optional daily notification.
//
// A FILE OF ITS OWN, mounted from `shell/SettingsScreen.tsx` in one line, rather
// than written inside it. Settings is the screen every workstream in this client
// has to touch, and a feature that arrives as an import and a tag collides with
// nobody. It is also the shape `AppearanceGroup` takes inside that file, one
// directory along.
//
// WHAT IS ON SCREEN IS ONLY EVER WHAT CAN HAPPEN. Missing server support,
// iOS outside a Home Screen install, an unsupported browser, and blocked
// permission each suppress the On action. A subscription already stored for
// this browser keeps its Off action in every one of those states.
//
// The order matters: a missing key pair means nothing can work anywhere, so it
// outranks the iPhone advice - somebody following the Home Screen instructions
// on a server with no keys would do the work and still get nothing.

import { Icon } from '../day/Icon.tsx';
import { TIME_STEP_SECONDS, minuteOfTime, timeOfMinute } from './push-support.ts';
import { useDailyNotification } from './useDailyNotification.ts';

interface Props {
  /** Only read while Settings is the screen that is on, as the board's read is. */
  active: boolean;
  onSignedOut: () => void;
}

export function NotificationSetting({ active, onSignedOut }: Props) {
  const state = useDailyNotification({ active, onSignedOut });
  const { setting, support, blocked, on, minute, busy, problem } = state;
  const canTurnOn = setting?.configured === true && support.kind === 'ready' && !blocked;

  return (
    <div className="setgroup">
      {/* An h2 under the screen's own h1, so the heading order has no gap in it -
          the same rule every other group on this screen follows. */}
      <h2>Notifications</h2>

      <div className="setlist setnotify">
        <div className="setrow">
          <Icon name="bell" />
          <span className="st">
            <b>Daily notification</b>
            <span>Once a day, how much is on today.</span>
          </span>

          {setting !== null && (canTurnOn || on) && (
            <div className="seg segmini" role="group" aria-label="Daily notification">
              {/* `aria-pressed` rather than a checkbox or a radio pair, which is
                  this client's settled way of drawing a chooser
                  (`edit/Sheet.tsx`, and the Wording control above).

                  THE OPTION ALREADY CHOSEN IS NOT A CONTROL, and this pair is
                  the one on this screen that has to say so. The theme and the
                  wording beside it write a preference, so pressing the one you
                  are on rewrites the same value and costs nothing. Neither of
                  these is that: On asks the browser for permission, registers a
                  worker and re-subscribes, and Off deletes a row. A button that
                  looks pressable and quietly does all of that to somebody who
                  was reading rather than choosing is a control that acts when it
                  should do nothing. Turning it on again after the deployment's
                  key pair changed is still reachable, as Off and then On - which
                  is what repairs it anyway (`useDailyNotification.ts`,
                  `madeWith`). */}
              <button
                type="button"
                aria-pressed={!on}
                disabled={busy || !on}
                onClick={state.disable}
              >
                Off
              </button>
              {canTurnOn && (
                <button
                  type="button"
                  aria-pressed={on}
                  disabled={busy || on}
                  onClick={state.enable}
                >
                  On
                </button>
              )}
            </div>
          )}
        </div>

        {/* THE TIME APPEARS ONLY ONCE IT IS ON, which keeps turning it on to one
            press. Somebody who wants a different time is one field away, and
            somebody who does not never sees one - general-audience defaults,
            with the depth one level down (AGENTS.md). */}
        {on && canTurnOn && (
          <div className="setrow">
            <Icon name="clock" />
            <span className="st">
              <b>
                <label htmlFor="pn-notify-time">Time</label>
              </b>
              {/* The zone is the account's and is resolved on the server, so this
                  says "your own" rather than naming one this client would have to
                  work out - and working out which day it is, is the one thing no
                  client here does (AGENTS.md). */}
              <span>In your own time zone.</span>
            </span>
            <input
              id="pn-notify-time"
              className="timefield"
              type="time"
              step={TIME_STEP_SECONDS}
              value={timeOfMinute(minute)}
              disabled={busy}
              onChange={(event) => {
                const next = minuteOfTime(event.target.value);
                // A cleared field is not a time. Ignored rather than treated as
                // midnight, which is a real choice somebody may have made.
                if (next !== null) state.chooseMinute(next);
              }}
            />
          </div>
        )}
      </div>

      {/* Exactly one support line is ever drawn. `role="alert"` only on the
          failure, because the status lines are on screen at mount and a live
          region that arrives with its text already in it is routinely not
          announced. */}
      {setting !== null && !setting.configured && (
        <p className="finetext">
          Notifications are not set up on this server, so it cannot send one. Whoever runs it can
          switch them on by generating a key pair; nothing about your account changes either way.
        </p>
      )}

      {setting !== null && setting.configured && support.kind === 'ios-needs-install' && (
        <p className="finetext">
          On iPhone and iPad, notifications require iOS or iPadOS 16.4 or later and a Home Screen
          install - that is Apple&rsquo;s rule for every website. After updating if needed, open the
          Share menu, choose <b>Add to Home Screen</b>, then open Perfect Note from there and come
          back to this screen.
        </p>
      )}

      {setting !== null && setting.configured && support.kind === 'ios-needs-update' && (
        <p className="finetext">
          Notifications require iOS or iPadOS 16.4 or later. Update this device first, then add
          Perfect Note to the Home Screen and open it from there.
        </p>
      )}

      {setting !== null && setting.configured && support.kind === 'ios-installed-unsupported' && (
        <p className="finetext">
          Perfect Note is already open from the Home Screen, but this device does not provide Web
          Push here. Notifications require iOS or iPadOS 16.4 or later; update the device and open
          Perfect Note from the Home Screen again.
        </p>
      )}

      {setting !== null && setting.configured && support.kind === 'unsupported' && (
        <p className="finetext">
          This browser cannot show notifications. Perfect Note works exactly the same without them,
          and another browser on the same account can still have one.
        </p>
      )}

      {setting !== null && setting.configured && support.kind === 'ready' && blocked && (
        <p className="finetext">
          Notifications are blocked for this site in your browser&rsquo;s own settings, so this
          cannot be switched on from here. Allow them for this site and come back - browsers only
          ask once, which is why Perfect Note never asks until you press On.
        </p>
      )}

      {setting !== null && setting.configured && support.kind === 'ready' && !blocked && (
        <p className="finetext">
          Off unless you turn it on, and it belongs to this browser - a phone and a laptop are set
          separately. It says how many things are on your day and never what they are, because a
          notification is read off a lock screen. You get one only on days that have something on
          them, and never anything else: no reminders to hurry up, and nothing in the evening about
          what you did not finish.
        </p>
      )}

      {problem !== null && (
        <p className="sheetproblem" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
